import { createRoot } from "react-dom/client";
import type { ReactNode } from "react";
import globalsCss from "@/globals.css?inline";
import loadingOverlayCss from "@/components/LoadingOverlay.css?inline";
import catPeekCss from "@/components/price-customization/CatPeek.css?inline";
import dogPeekCss from "@/components/price-customization/DogPeek.css?inline";
import { ShadowRootProvider } from "./ShadowRootContext";
import { hashQueryParams } from "@/utils/urlParams";

/**
 * The funnel is embedded into pages with their own global CSS (simpego.ch ships element selectors
 * like `h3 { font-size: 40px }` and class names that collide with Tailwind's, e.g. `.text-base`).
 * Rendering into a shadow root keeps that CSS out, and keeps Tailwind's preflight from restyling
 * the embedding page.
 */

/** `@property` registrations are ignored inside shadow trees, but Tailwind's `--tw-*` defaults
 * (shadows, rings, transforms, ...) depend on them — so they are registered on the document. */
const registerPropertyRules = (css: string) => {
  const rules = css.match(/@property\s+--[\w-]+\s*\{[^}]*\}/g);
  if (!rules) return;
  const style = document.createElement("style");
  style.setAttribute("data-simpego-funnel", "properties");
  style.textContent = rules.join("\n");
  document.head.appendChild(style);
};

const DEFAULT_PARTNER = "simpego";

const getPartnerName = (): string => hashQueryParams().get("s") || DEFAULT_PARTNER;

const addStyle = (shadowRoot: ShadowRoot, css: string) => {
  const style = document.createElement("style");
  style.textContent = css;
  shadowRoot.appendChild(style);
};

const loadLink = (parent: Node, href: string): Promise<void> =>
  new Promise((resolve) => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    link.onload = () => resolve();
    link.onerror = () => {
      console.error(`Could not load the partner styles from ${href}`);
      resolve();
    };
    parent.appendChild(link);
  });

/** Loads the whitelabel stylesheet and resolves once it has loaded, so the funnel is never rendered unstyled. The
 * file is expected to exist. A `<link>` needs no CORS headers, unlike `fetch`.
 *
 * It is loaded twice, from the same URL (one request): into the shadow root, where its `:host(.s-<partner>)` rules
 * apply to the funnel, and into the document, because `@font-face` and `@import` (the partner's fonts) have no effect
 * inside a shadow tree. In the document the `:host` rules match nothing, so nothing leaks into the embedding page. */
const loadPartnerStyles = async (shadowRoot: ShadowRoot, hostElement: HTMLElement, partner: string) => {
  hostElement.classList.add(`s-${partner}`);
  // The build id makes browsers that still hold an older copy (the file used to be cached for a year) fetch the current one.
  const href = `${window.__env__?.widgetBasePath ?? ""}/${partner}-styles.css?v=${__BUILD_ID__}`;
  await Promise.all([loadLink(shadowRoot, href), loadLink(document.head, href)]);
};

export const mountFunnel = async (hostElement: HTMLElement, app: ReactNode) => {
  // Browser/Google translation rewrites text nodes React owns (-> `removeChild` NotFoundError), so opt out. The class is
  // honoured by Google Translate, the attribute by Chrome/Edge/Safari.
  hostElement.classList.add("notranslate");
  hostElement.setAttribute("translate", "no");

  const shadowRoot = hostElement.attachShadow({ mode: "open" });
  registerPropertyRules(globalsCss);
  addStyle(shadowRoot, [globalsCss, loadingOverlayCss, catPeekCss, dogPeekCss].join("\n"));
  await loadPartnerStyles(shadowRoot, hostElement, getPartnerName());

  const container = document.createElement("div");
  shadowRoot.appendChild(container);
  const calendarContainer = document.createElement("div");
  // Zero-height positioning origin for the flatpickr calendar (see `positionCalendar` in useFlatpickrField).
  calendarContainer.style.cssText = "position:relative;height:0;width:0;";
  shadowRoot.appendChild(calendarContainer);
  createRoot(container).render(
    <ShadowRootProvider value={{ shadowRoot, container, calendarContainer }}>{app}</ShadowRootProvider>,
  );
};
